/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  ThemeIcon
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconCurrencyDollar,
  IconFilter,
  IconRefresh,
  IconSearch,
  IconTarget,
  IconTrendingUp,
  IconUsers
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useCurrentUser, useRecommendedSignals } from "../../../../../hooks/QueryHooks";
import { RecommendedSignal, RecommendedSignalsResponse } from "../../../../../services/signals/recommendedSignalsEndpoint";
import useSignalStore from "../../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import notify from "../../../../../utils/notify";
import { LayoutTemplateIcon } from "lucide-react";

// Icon mapping based on category
const getCategoryIcon = (category: string) => {
  const iconMap: Record<string, any> = {
    engagement: IconTrendingUp,
    churn: IconAlertTriangle,
    expansion: IconUsers,
    health: IconTarget,
    onboarding: IconUsers,
    billing: IconCurrencyDollar,
  };
  return iconMap[category] || IconTarget;
};

// Color mapping based on category
const getCategoryColor = (category: string) => {
  const colorMap: Record<string, string> = {
    engagement: "blue",
    churn: "red",
    expansion: "teal",
    health: "violet",
    onboarding: "cyan",
    billing: "orange",
  };
  return colorMap[category] || "gray";
};

interface RecommendedSignalWithUI extends RecommendedSignal {
  icon?: any;
  color?: string;
}

interface RecommendedSignalsProps {
  onSignalSelected?: () => void;
}

const RecommendedSignals = ({ onSignalSelected }: RecommendedSignalsProps) => {
  const { setSignal } = useSignalStore();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const { data: user }: any = useCurrentUser({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingSignalId, setLoadingSignalId] = useState<string | null>(null);
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [shouldFetch, setShouldFetch] = useState(false);
  const MAX_REFRESH_COUNT = 3;
  const REFETCH_COOLDOWN = 30 * 60 * 1000; // 30 minutes in milliseconds

  // Check if we should auto-load based on cached data
  useEffect(() => {
    if (!workspace?.id) return;

    const cacheKey = `recommended-signals-${workspace.id}-all-10`;
    const lastFetchKey = `recommended-signals-last-fetch-${workspace.id}`;
    
    try {
      const cached = localStorage.getItem(cacheKey);
      
      // Only auto-load if there's no cached data
      if (!cached) {
        setShouldFetch(true);
        // Set last fetch timestamp only if we're actually fetching
        localStorage.setItem(lastFetchKey, Date.now().toString());
      }
    } catch {
      // If we can't access localStorage, don't auto-load
      setShouldFetch(false);
    }
  }, [workspace?.id]);

  // Fetch AI-powered recommended signals
  const {
    data: recommendedSignalsData,
    isLoading,
    isError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error,
    refetch,
    isFetching,
  } = useRecommendedSignals({
    workspaceId: workspace?.id,
    limit: 10,
    enabled: !!workspace?.id && shouldFetch,
    refreshCounter: refreshCount, // Pass refresh counter to force cache invalidation
  });

  // Check if refresh is on cooldown
  const getRefreshCooldownInfo = () => {
    if (!workspace?.id) return { onCooldown: false, minutesRemaining: 0 };
    
    const lastFetchKey = `recommended-signals-last-fetch-${workspace.id}`;
    try {
      const lastFetch = localStorage.getItem(lastFetchKey);
      if (lastFetch) {
        const timeSinceLastFetch = Date.now() - parseInt(lastFetch, 10);
        if (timeSinceLastFetch < REFETCH_COOLDOWN) {
          const minutesRemaining = Math.ceil((REFETCH_COOLDOWN - timeSinceLastFetch) / 60000);
          return { onCooldown: true, minutesRemaining };
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    return { onCooldown: false, minutesRemaining: 0 };
  };

  const { onCooldown, minutesRemaining } = getRefreshCooldownInfo();

  // Transform API data to include UI properties
  const recommendedSignals: RecommendedSignalWithUI[] = useMemo(() => {
    const data = recommendedSignalsData as RecommendedSignalsResponse | undefined;
    if (!data?.recommended_signals) return [];

    return data.recommended_signals.map((signal: RecommendedSignal) => ({
      ...signal,
      icon: getCategoryIcon(signal.category),
      color: getCategoryColor(signal.category),
    }));
  }, [recommendedSignalsData]);

  const categories = [
    { value: "all", label: "All" },
    { value: "engagement", label: "Engagement" },
    { value: "churn", label: "Churn Prevention" },
    { value: "expansion", label: "Expansion" },
    { value: "health", label: "Account Health" },
    { value: "onboarding", label: "Onboarding" },
    { value: "billing", label: "Billing" }
  ];

  const filteredSignals = useMemo(() =>
    recommendedSignals.filter(signal => {
      const categoryMatch = selectedCategory === "all" || signal.category === selectedCategory;
      const searchMatch = !searchQuery
        || signal.title.toLowerCase().includes(searchQuery.toLowerCase())
        || signal.description.toLowerCase().includes(searchQuery.toLowerCase());
      return categoryMatch && searchMatch;
    }), [recommendedSignals, selectedCategory, searchQuery]);

  const handleSelectSignal = (signal: RecommendedSignalWithUI) => {
    setLoadingSignalId(signal.id);

    // Simulate loading time for better UX
    setTimeout(() => {
      setSignal({
        ...signal.signal_config,
        filters: signal.signal_config.filters as any,
        metrics: [],
        is_precreated: true,
        status: "paused",
        time_window: "",
        time_zone: workspace?.workspace_time_zone || "",
        owner_id: user?.user_id || "",
        workspace_id: workspace?.id || "",
        responseData: {},
      });
      setLoadingSignalId(null);
      setSelectedSignalId(signal.id);

      notify({
        heading: "Template Loaded",
        messageList: [`${signal.title} template loaded successfully!`],
        type: "success",
      });

      if (onSignalSelected) {
        setTimeout(() => {
          onSignalSelected();
        }, 400);
      }
    }, 600);
  };

  const handleRefresh = () => {
    if (!workspace?.id) return;

    const lastFetchKey = `recommended-signals-last-fetch-${workspace.id}`;
    
    // Check cooldown period
    try {
      const lastFetch = localStorage.getItem(lastFetchKey);
      if (lastFetch) {
        const timeSinceLastFetch = Date.now() - parseInt(lastFetch, 10);
        if (timeSinceLastFetch < REFETCH_COOLDOWN) {
          const minsRemaining = Math.ceil((REFETCH_COOLDOWN - timeSinceLastFetch) / 60000);
          notify({
            heading: "Please Wait",
            messageList: [`You can refresh again in ${minsRemaining} minute${minsRemaining !== 1 ? 's' : ''}.`],
            type: "failure",
          });
          return;
        }
      }
    } catch {
      // Ignore localStorage errors
    }

    if (refreshCount >= MAX_REFRESH_COUNT) {
      notify({
        heading: "Refresh Limit Reached",
        messageList: ["You've reached the maximum number of refreshes for this session."],
        type: "failure",
      });
      return;
    }

    // Clear localStorage cache for this query
    const cacheKey = `recommended-signals-${workspace?.id}-all-10`;
    try {
      localStorage.removeItem(cacheKey);
      localStorage.setItem(lastFetchKey, Date.now().toString());
    } catch (e) {
      // Ignore localStorage errors
    }

    // Enable fetching and increment refresh counter
    setShouldFetch(true);
    setRefreshCount(prev => prev + 1);

    // Refetch the data
    refetch().catch(() => { });

    notify({
      heading: "Refreshing Templates",
      messageList: [`Fetching fresh templates... (${MAX_REFRESH_COUNT - refreshCount - 1} refreshes remaining)`],
      type: "info",
    });
  };

  return (
    <Box className="flex flex-col overflow-hidden h-full p-6 bg-white rounded-lg border border-gray-200">
      {/* Header & Filters Section */}
      <div className="mb-3 space-y-4">
        <Tabs
          value={selectedCategory}
          onChange={setSelectedCategory}
          variant="pills"
          radius="md"
          classNames={{
            list: "border-b border-gray-200 pb-2 gap-4",
            tab: "text-gray-500 font-medium text-sm px-0 pb-3 bg-transparent hover:bg-transparent hover:text-gray-800 border-b-2 border-transparent data-[active=true]:border-purple-500 data-[active=true]:text-gray-900 h-auto rounded-none",
          }}
        >
          <Tabs.List>
            {categories.map((category) => (
              <Tabs.Tab key={category.value} value={category.value}>
                {category.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        <TextInput
          placeholder="Search for a signal template..."
          leftSection={<IconSearch size={16} className="text-gray-400" />}
          size="sm"
          radius="md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          className="w-full"
          styles={{ input: { borderColor: '#e5e7eb' } }}
        />

        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={handleRefresh}
            loading={isFetching}
            disabled={isLoading || refreshCount >= MAX_REFRESH_COUNT || onCooldown}
            color="gray"
          >
            {onCooldown 
              ? `Available in ${minutesRemaining}m` 
              : refreshCount > 0 
                ? `Refresh (${MAX_REFRESH_COUNT - refreshCount} left)` 
                : 'Refresh List'}
          </Button>
        </Group>
      </div>

      {/* Content Section */}
      <div className="flex-1 overflow-y-auto px-1 pb-4">
        {isLoading ? (
          <Center h={400}>
            <Stack align="center" gap="md">
              <Loader type="dots" size="lg" />
              <Text size="sm" c="dimmed">Loading templates...</Text>
            </Stack>
          </Center>
        ) : isError ? (
          <Center h={400}>
            <Stack align="center" gap="md">
              <ThemeIcon size="xl" radius="circle" color="red" variant="light">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <Text size="md" fw={600} c="gray.9">Failed to load</Text>
              <Button variant="light" size="sm" color="red" onClick={() => { refetch().catch(() => { }); }}>Retry</Button>
            </Stack>
          </Center>
        ) : filteredSignals.length === 0 ? (
          <Center h={400}>
            <Stack align="center" gap="md" maw={400} ta="center">
              <ThemeIcon size="48" radius="circle" color="gray" variant="light">
                <IconFilter size={24} />
              </ThemeIcon>
              <Text fw={600} size="md" c="gray.8">No templates found</Text>
              <Button
                variant="subtle"
                size="sm"
                color="gray"
                onClick={() => {
                  setSelectedCategory("all");
                  setSearchQuery("");
                }}
              >
                Clear Filters
              </Button>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing="lg">
            {filteredSignals.map((signal) => {
              const IconComponent = signal.icon;
              const isSelected = selectedSignalId === signal.id;
              const isLoadingThis = loadingSignalId === signal.id;

              return (
                <Paper
                  key={signal.id}
                  p="xl"
                  radius="md"
                  withBorder
                  className={`
                    relative overflow-hidden transition-all duration-200 flex flex-col h-full border border-gray-100
                    ${'border-gray-200 bg-white hover:shadow-sm hover:border-gray-300'}
                  `}
                >
                  {/* Subtle color stripe at the top on hover or selection */}
                  <div
                    className={`absolute top-0 left-0 w-full h-1 transition-opacity duration-200 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    style={{ backgroundColor: `var(--mantine-color-${signal.color}-5)` }}
                  />

                  <Stack h="100%" justify="space-between" gap="lg">
                    <div>
                      <Group justify="space-between" align="start" mb="md">
                        <ThemeIcon
                          size={56}
                          radius="md"
                          variant="light"
                          color={signal.color}
                          className={`bg-opacity-10 ${isSelected ? 'bg-opacity-20' : ''}`}
                        >
                          <IconComponent size={28} stroke={1.5} />
                        </ThemeIcon>

                        {signal.popular && (
                          <Badge
                            size="xs"
                            variant="light"
                            color="blue"
                            className="text-[10px] font-medium"
                          >
                            MOST POPULAR
                          </Badge>
                        )}
                      </Group>

                      <Text fw={600} size="md" mb={4} c="gray.9">
                        {signal.title}
                      </Text>

                      <Text size="sm" c="dimmed" lh={1.5} lineClamp={3} className="text-gray-500">
                        {signal.description}
                      </Text>
                    </div>

                    <Button
                      fullWidth
                      variant={isSelected ? "light" : "default"}
                      loading={isLoadingThis}
                      size="sm"
                      radius="md"
                      leftSection={!isLoadingThis ? <LayoutTemplateIcon className="w-4 h-4" /> : <IconCheck className="w-4 h-4" />}
                      onClick={() => !isLoadingThis && handleSelectSignal(signal)}
                    >
                      {isLoadingThis ? 'Setting up...' : isSelected ? 'Template Applied' : 'Use Template'}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </SimpleGrid>
        )}
      </div>
    </Box>
  );
};

export default RecommendedSignals;
